/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Utils } from "@tldraw/core";
import Vec from "@tldraw/vec";
import type { TldrawApp } from "pages/flyer/builder/drawer/state/internal";
import { TLDR } from "pages/flyer/builder/drawer/state/TLDR";
import { DistributeType, TDShape, TDShapeType, TldrawCommand } from "pages/flyer/builder/drawer/types";

export function distributeShapes(app: TldrawApp, ids: string[], type: DistributeType): TldrawCommand {
  const { currentPageId } = app;

  const initialShapes = ids.map((id) => app.getShape(id));

  const deltaMap = Object.fromEntries(getDistributions(initialShapes, type).map((d) => [d.id, d]));

  const { before, after } = TLDR.mutateShapes(
    app.state,
    ids.filter((id) => deltaMap[id] !== undefined),
    (shape) => ({ point: deltaMap[shape.id].next }),
    currentPageId
  );

  initialShapes.forEach((shape) => {
    if (shape.type === TDShapeType.Group) {
      const delta = Vec.sub(after[shape.id].point!, before[shape.id].point!);

      shape.children.forEach((id) => {
        const child = app.getShape(id);
        before[child.id] = { point: child.point };
        after[child.id] = { point: Vec.add(child.point, delta) };
      });

      delete before[shape.id];
      delete after[shape.id];
    }
  });

  return {
    id: "distribute",
    before: {
      document: {
        pages: {
          [currentPageId]: { shapes: before },
        },
        pageStates: {
          [currentPageId]: {
            selectedIds: ids,
          },
        },
      },
    },
    after: {
      document: {
        pages: {
          [currentPageId]: { shapes: after },
        },
        pageStates: {
          [currentPageId]: {
            selectedIds: ids,
          },
        },
      },
    },
  };
}

function getDistributions(initialShapes: TDShape[], type: DistributeType) {
  const entries = initialShapes.map((shape) => {
    const utils = TLDR.getShapeUtil(shape);
    return {
      id: shape.id,
      point: [...shape.point],
      bounds: utils.getBounds(shape),
      center: utils.getCenter(shape),
    };
  });

  const len = entries.length;
  const commonBounds = Utils.getCommonBounds(entries.map(({ bounds }) => bounds));

  const results: { id: string; prev: number[]; next: number[] }[] = [];

  switch (type) {
    case DistributeType.Horizontal: {
      // Sort shapes by their center X position
      const sortedEntries = entries.sort((a, b) => a.center[0] - b.center[0]);

      // Calculate total width of all shapes
      const totalWidth = sortedEntries.reduce((sum, entry) => sum + entry.bounds.width, 0);

      // Calculate available space for distribution
      const availableSpace = commonBounds.width - totalWidth;

      // Calculate equal spacing between shapes
      const spacing = len > 1 ? availableSpace / (len - 1) : 0;

      // Position shapes with equal spacing
      let currentX = commonBounds.minX;

      sortedEntries.forEach(({ id, point, bounds }) => {
        results.push({
          id,
          prev: point,
          next: [currentX, bounds.minY],
        });
        currentX += bounds.width + spacing;
      });
      break;
    }
    case DistributeType.Vertical: {
      // Sort shapes by their center Y position
      const sortedEntries = entries.sort((a, b) => a.center[1] - b.center[1]);

      // Calculate total height of all shapes
      const totalHeight = sortedEntries.reduce((sum, entry) => sum + entry.bounds.height, 0);

      // Calculate available space for distribution
      const availableSpace = commonBounds.height - totalHeight;

      // Calculate equal spacing between shapes
      const spacing = len > 1 ? availableSpace / (len - 1) : 0;

      // Position shapes with equal spacing
      let currentY = commonBounds.minY;

      sortedEntries.forEach(({ id, point, bounds }) => {
        results.push({
          id,
          prev: point,
          next: [bounds.minX, currentY],
        });
        currentY += bounds.height + spacing;
      });
      break;
    }
  }

  return results;
}
